import { useMemo } from "react";
import { useUserStore } from "../../../stores/user.store";
import { useSettings } from "../../../hooks/useSetting";
import { useBeneficiaryTerm } from "../../../hooks/useBeneficiaryTerm";
import { ISetting } from "../../../domain/setting.interface";

/**
 * The Participant column's header and cell contents (SIMS-8895 item 5).
 *
 * Two account settings decide both, and every tab of the consolidated report
 * that shows a participant has to read them the same way — hence one hook
 * rather than the rule repeated per tab:
 *
 * - `beneficiaryTerms` names the column. The report hard-coded "Participant",
 *   so an account that calls them Members or Clients saw the wrong word in this
 *   report and the right one everywhere else.
 * - `displayBeneficiaryName` decides what the cell holds. When it is off the
 *   account has asked not to surface names, so the cell shows the client id
 *   number instead. This is the account's privacy choice, not a formatting
 *   preference — defaulting the wrong way would show names the account
 *   suppressed, so an unresolved setting is treated as "do not show the name".
 */
export interface ParticipantColumn {
	/** Header label — the account's singular term for a beneficiary. */
	label: string;
	/**
	 * Whether the account permits showing beneficiary names (SIMS-9019).
	 *
	 * Exposed so a table that has its own CIN column can DROP the name column
	 * outright instead of rendering a second copy of the CIN — which is what
	 * `display` below would give it. The Participant List behaves the same way
	 * (`pages/Participants/List.tsx`), and the ticket points at that screen as
	 * the reference.
	 */
	showName: boolean;
	/**
	 * The cell value for one row.
	 *
	 * Returns an empty string rather than a placeholder when there is nothing to
	 * show: SIMS-8895 replaced "(Unknown)", which read as a value the record does
	 * not actually hold.
	 */
	display: (participantName: string, participantCin: string) => string;
	/**
	 * One line that identifies the participant on its own (SIMS-9019).
	 *
	 * `display` fills a cell that sits in a row full of other context, so it can
	 * afford to hold either the name or the CIN. A dropdown option has no such
	 * row: it is the entire basis for choosing, and the ticket asks that it be
	 * searchable by CIN or by name. So this joins both, CIN first — the order the
	 * Participant List uses.
	 *
	 * The name is omitted when the account has turned it off, exactly as in
	 * `display`. Whichever parts are missing simply drop out, so a participant
	 * with no name recorded reads as their CIN rather than as a dangling dash.
	 */
	optionLabel: (participantName: string, participantCin: string) => string;
}

/** Separator between CIN and name in `optionLabel`. */
const OPTION_LABEL_SEPARATOR = " — ";

export function useParticipantColumn(): ParticipantColumn {
	const { userInfo } = useUserStore();
	const { data: settings } = useSettings(userInfo?.id);
	const { singular } = useBeneficiaryTerm();

	const { displayBeneficiaryName } = settings || ({} as ISetting);

	// `=== true`, not truthiness: while the settings request is in flight
	// `displayBeneficiaryName` is undefined, and falling back to showing the name
	// would flash it on screen for accounts that turned it off.
	const showName = displayBeneficiaryName === true;

	// Memoised on the two values it actually derives from. Callers put this in
	// the dependency list of the `useMemo` that maps their rows, so returning a
	// fresh object every render would rebuild every table row on every render.
	return useMemo(
		() => ({
			label: singular,
			showName,
			display: (participantName: string, participantCin: string): string =>
				showName ? (participantName ?? "") : (participantCin ?? ""),
			optionLabel: (participantName: string, participantCin: string): string =>
				[participantCin ?? "", showName ? (participantName ?? "") : ""]
					.filter(Boolean)
					.join(OPTION_LABEL_SEPARATOR),
		}),
		[singular, showName],
	);
}
